/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2022, Bulb Inc.  For more information, please email contact@hellobulb.com.
 *
 *                _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 *
 *
 */

/**
 * @module carousel-transition/bulbcarouseltransitionUi
 */

import {
  Plugin,
  ViewModel,
  createDropdown,
  addListToDropdown,
  Collection,
  type DropdownView,
  type Locale,
  type LocaleTranslate,
  type Command,
} from 'ckeditor5';

import transitionIcon from '../../../assets/toolbar_icons/carousel-transition/carousel-transition.svg';
import cube from '../../../assets/toolbar_icons/carousel-transition/carousel-cube.svg';
import fade from '../../../assets/toolbar_icons/carousel-transition/carousel-fade.svg';
import slide from '../../../assets/toolbar_icons/carousel-transition/carousel-slide.svg';
import flip from '../../../assets/toolbar_icons/carousel-transition/carousel-flip.svg';
import { BulbCarouselTransitionCommand } from './bulbcarouseltranisitioncommand';
import { BULB_CAROUSEL_TRANSITION } from '../../../editorcustomconfig.constant';

export const BULB_CAROUSEL_TRANSITION_CONST = 'bulbCarouselTransition';
/**
 * The bulb carousel transition dropdown.
 *
 * @extends Plugin
 */
export default class BulbCarouselTransitionUi extends Plugin {

  /**
   * @inheritDoc
   */
  public static get pluginName() {
    return 'BulbCarouselTransitionUi' as const;
  }

  /**
   * @inheritDoc
   */
  public init(): void {
    const { editor } = this;
    const { t } = editor;
    const { options } = BULB_CAROUSEL_TRANSITION;
    editor.commands.add(BULB_CAROUSEL_TRANSITION_CONST, new BulbCarouselTransitionCommand(editor));
    const command: Command = editor.commands.get(BULB_CAROUSEL_TRANSITION_CONST);

    // Register UI component.
    editor.ui.componentFactory.add(BULB_CAROUSEL_TRANSITION_CONST, (locale: Locale): DropdownView => {
      const dropdownView = createDropdown(locale);

      // Add transitions option to dropdown's toolbar.
      addListToDropdown(dropdownView, this.prepareListOptions(options, t, command) as any);

      // Configure dropdown properties and behavior.
      dropdownView.buttonView.set({
        label: t('Transition'),
        icon: transitionIcon,
        tooltip: true,
      });
      dropdownView.bind('isEnabled').to(command);

      dropdownView.buttonView.on('execute', () => {
        const elem = editor.model.document.selection.getSelectedElement();
        const contentBlock = CKEBulbData.page.unit.contentBlocks[elem.getAttribute('bulb-block-id') as string];
        // setTimeout is needed here because initially when we have implemented hidding cube
        // transition feature may be performance of ckeditor plugin was slow because of that it
        // gets enough time to find cube transition element on dropdown button click but now after
        // ckeditor update to improve performance they have seperated toolbar and dropdown implementation
        // due to which we are not able to find the transition element on button click there is delay in that.
        setTimeout(() => {
          if (contentBlock) {
            const element = document.querySelector('.animation_CUBE') as HTMLElement;
            if (element) {
              element.style.display = (contentBlock.orientation !== 'SQUARE') ? 'none' : 'block';
            }
          }
        });
      });

      dropdownView.extendTemplate({
        attributes: {
          class: 'ck_carousel_transition',
        },
      });
      this.listenTo(dropdownView, 'execute', (evt) => {
        const source = evt.source as { commandName: string; commandParam: string };
        editor.execute(source.commandName, source.commandParam.toLowerCase());
        editor.editing.view.focus();
      });

      return dropdownView;
    });
  }

  /**
   * Get icons based on transition.
   *
   * @param name Transition.
   * @returns {null|string}
   */
  private getIcons(name): null | string {
    switch (name) {
      case 'FADE':
        return fade;
      case 'FLIP':
        return flip;
      case 'SLIDE':
        return slide;
      case 'CUBE':
        return cube;
      default:
        return null;
    }
  }

  /**
   * Prepare list option for dropdown.
   *
   * @param options Options for slides
   * @param t Localization object
   * @param command Command for transition
   * @returns {*}
   */
  private prepareListOptions(options: string[], t: LocaleTranslate, command: Command) {
    const itemDefinitions = new Collection();

    // Create dropdown items.
    for (const option of options) {
      const def = {
        type: 'button',
        model: new ViewModel({
          label: t(option),
          withText: true,
          tooltip: false,
          icon: this.getIcons(option),
          commandName: BULB_CAROUSEL_TRANSITION_CONST,
          commandParam: option,
          class: `animation_${option}`,
        }),
      };
      def.model.bind('isOn').to(command, 'value', (value) => {
        if (value) {
          return (option.toLowerCase() === value);
        }
        return (option === 'SLIDE');
      });
      itemDefinitions.add(def);
    }
    return itemDefinitions;
  }

}
